import React, { useState, useEffect } from 'react';
import { stockIssueAPI, propertyAPI, storeAPI, itemAPI, unitAPI, stockAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import { SearchableItemSelect } from '../components/SearchableItemSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, ArrowUpFromLine, Loader2, Trash2, Eye, Pencil, Download, Save } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatNumber, ISSUE_TO_OPTIONS } from '../lib/utils';

export const StockIssuePage = () => {
  const [records, setRecords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    from_store_id: '',
    issue_to: '',
    issue_to_detail: '',
    items: [{ item_id: '', quantity: '', unit_id: '' }],
    purpose: '',
    received_by: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, propertiesRes, storesRes, itemsRes, unitsRes, stockRes] = await Promise.all([
        stockIssueAPI.getAll(),
        propertyAPI.getAll({ status: 'active' }),
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        stockAPI.getAll()
      ]);
      setRecords(recordsRes.data);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setStock(stockRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setFormData({
      property_id: properties[0]?.id || '',
      from_store_id: '',
      issue_to: '',
      issue_to_detail: '',
      items: [{ item_id: '', quantity: '', unit_id: '' }],
      purpose: '',
      received_by: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_id: '', quantity: '', unit_id: '' }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'item_id') {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem?.default_unit_id) {
        newItems[index].unit_id = selectedItem.default_unit_id;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const getAvailableStock = (itemId, storeId) => {
    const stockRecord = stock.find(s => s.item_id === itemId && s.store_id === storeId);
    return stockRecord?.quantity || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validItems = formData.items.filter(item => item.item_id && item.quantity);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        items: validItems.map(item => ({
          item_id: item.item_id,
          quantity: parseFloat(item.quantity) || 0,
          unit_id: item.unit_id
        }))
      };
      
      await stockIssueAPI.create(data);
      toast.success('Stock issue recorded successfully');
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setEditItems(record.items?.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      unit_name: item.unit_name,
      quantity: item.quantity
    })) || []);
    setEditDialogOpen(true);
  };

  const handleEditItemChange = (index, value) => {
    const newItems = [...editItems];
    newItems[index].quantity = parseFloat(value) || 0;
    setEditItems(newItems);
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    
    try {
      setSaving(true);
      await stockIssueAPI.update(editingRecord.id, {
        items: editItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity
        }))
      });
      toast.success('Stock Issue updated successfully');
      setEditDialogOpen(false);
      setEditingRecord(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (record) => {
    try {
      setDownloading(record.id);
      const response = await stockIssueAPI.downloadPDF(record.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `StockIssue_${record.transaction_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const filteredStores = formData.property_id
    ? stores.filter(s => s.property_id === formData.property_id)
    : stores;

  // Status badge colors
  const getStatusBadge = (status) => {
    const statusConfig = {
      'issued': { label: 'Issued', className: 'bg-blue-100 text-blue-700 border-blue-300' },
      'pending_acknowledgement': { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-300' },
      'accepted': { label: 'Accepted', className: 'bg-green-100 text-green-700 border-green-300' },
      'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-300' },
      'partially_accepted': { label: 'Partial', className: 'bg-orange-100 text-orange-700 border-orange-300' },
      'disputed': { label: 'Disputed', className: 'bg-purple-100 text-purple-700 border-purple-300' }
    };
    const config = statusConfig[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700' };
    return <Badge variant="outline" className={`${config.className} text-xs`}>{config.label}</Badge>;
  };

  const columns = [
    { key: 'transaction_number', label: 'Transaction #', render: (val) => <span className="font-medium font-mono">{val}</span> },
    { key: 'date', label: 'Date', render: (val) => formatDate(val) },
    { key: 'property_name', label: 'Property', render: (val) => val || '-' },
    { key: 'from_store_name', label: 'From Store', render: (val) => val || '-' },
    { 
      key: 'issue_to', 
      label: 'Issue To', 
      render: (val, row) => {
        if (val === 'store' && row.to_store_name) {
          return <span className="text-sm">{row.to_store_name}</span>;
        }
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">{val}</span>;
      }
    },
    { key: 'items', label: 'Items', render: (val) => `${val?.length || 0} items` },
    { 
      key: 'status', 
      label: 'Status', 
      render: (val) => getStatusBadge(val)
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Stock Issue</h1>
          <p className="text-muted-foreground mt-1">Issue stock to departments and teams</p>
        </div>
        <Button onClick={handleOpenDialog} className="rounded-full" data-testid="add-stock-issue-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Issue Entry
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable
            module="stock_issue"
            data={records}
            loading={loading}
            title="Stock Issues"
            columns={columns}
            renderActions={(record) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedRecord(record);
                    setViewDialogOpen(true);
                  }}
                  title="View Details"
                  data-testid={`view-issue-${record.id}`}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEditRecord(record)}
                  title="Edit Issue"
                  data-testid={`edit-issue-${record.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownloadPDF(record)}
                  disabled={downloading === record.id}
                  title="Download PDF"
                  data-testid={`download-issue-${record.id}`}
                >
                  {downloading === record.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* New Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Stock Issue Entry</DialogTitle>
            <DialogDescription>
              Issue stock from a store to departments or teams
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Property *</Label>
                  <Select
                    value={formData.property_id}
                    onValueChange={(value) => setFormData({ ...formData, property_id: value, from_store_id: '' })}
                  >
                    <SelectTrigger data-testid="issue-property-select">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>From Store *</Label>
                  <Select
                    value={formData.from_store_id}
                    onValueChange={(value) => setFormData({ ...formData, from_store_id: value })}
                  >
                    <SelectTrigger data-testid="issue-store-select">
                      <SelectValue placeholder="Select store" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredStores.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issue To *</Label>
                  <Select
                    value={formData.issue_to}
                    onValueChange={(value) => setFormData({ ...formData, issue_to: value })}
                  >
                    <SelectTrigger data-testid="issue-to-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {ISSUE_TO_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Received By</Label>
                  <Input
                    value={formData.received_by}
                    onChange={(e) => setFormData({ ...formData, received_by: e.target.value })}
                    placeholder="Person receiving the stock"
                    data-testid="issue-received-by-input"
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddItem} data-testid="add-issue-item-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[35%]">Item</TableHead>
                        <TableHead className="w-[20%]">Available</TableHead>
                        <TableHead className="w-[15%]">Quantity</TableHead>
                        <TableHead className="w-[20%]">Unit</TableHead>
                        <TableHead className="w-[10%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const availableQty = getAvailableStock(item.item_id, formData.from_store_id);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <SearchableItemSelect
                                items={items}
                                value={item.item_id}
                                onSelect={(value) => handleItemChange(index, 'item_id', value)}
                                placeholder="Search & select item..."
                                testId={`issue-item-${index}-select`}
                              />
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${availableQty <= 0 ? 'text-destructive' : 'text-green-600'}`}>
                                {formatNumber(availableQty)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                max={availableQty}
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                data-testid={`issue-qty-${index}-input`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={item.unit_id || "none"}
                                onValueChange={(value) => handleItemChange(index, 'unit_id', value === "none" ? "" : value)}
                              >
                                <SelectTrigger data-testid={`issue-unit-${index}-select`}>
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select unit</SelectItem>
                                  {units.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {formData.items.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveItem(index)}
                                  data-testid={`remove-issue-item-${index}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Purpose / Remarks</Label>
                <Input
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Purpose of this issue"
                  data-testid="issue-purpose-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="issue-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-issue-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Stock Issue Details</DialogTitle>
            <DialogDescription>
              Transaction: {selectedRecord?.transaction_number}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedRecord.date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Property</p>
                  <p className="font-medium">{selectedRecord.property_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">From Store</p>
                  <p className="font-medium">{selectedRecord.from_store_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Issue To</p>
                  <p className="font-medium">{selectedRecord.issue_to}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Issued By</p>
                  <p className="font-medium">{selectedRecord.issued_by_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Received By</p>
                  <p className="font-medium">{selectedRecord.received_by || '-'}</p>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRecord.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.quantity)}</TableCell>
                        <TableCell>{item.unit_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {selectedRecord.purpose && (
                <div>
                  <p className="text-muted-foreground text-sm">Purpose</p>
                  <p className="text-sm">{selectedRecord.purpose}</p>
                </div>
              )}
              {selectedRecord.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Stock Issue
            </DialogTitle>
            <DialogDescription>
              Update issued quantities. Changes will adjust stock automatically.
            </DialogDescription>
          </DialogHeader>
          
          {editingRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm p-3 bg-muted rounded-lg">
                <div>
                  <span className="text-muted-foreground">Transaction #:</span>{' '}
                  <span className="font-medium">{editingRecord.transaction_number}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Issue To:</span>{' '}
                  <span className="font-medium">{editingRecord.issue_to}</span>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right w-28">Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleEditItemChange(idx, e.target.value)}
                            className="w-24 h-8 text-right"
                            data-testid={`edit-issue-qty-${idx}`}
                          />
                        </TableCell>
                        <TableCell>{item.unit_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-issue-edit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockIssuePage;
